// WhatsApp message text → React nodes, for the Ask AICA WhatsApp chat view.
//
// React nodes only, never dangerouslySetInnerHTML: the body is untrusted input (lenders type it),
// so it is never parsed as HTML. Inline marks follow WhatsApp's word-boundary rule: an opener must
// start the line or follow a non-word character and a closer must end the line or precede one, so
// `snake_case_name` and `Bank_Statement_FY24.pdf` stay literal. Independent of the Comments drawer.

import React from 'react';
import styles from './WhatsAppChat.module.scss';

const MARKS: ReadonlyArray<{ char: string; render: (c: React.ReactNode, k: string) => React.ReactElement }> = [
    { char: '*', render: (c, k) => <strong key={k}>{c}</strong> },
    { char: '_', render: (c, k) => <em key={k}>{c}</em> },
    { char: '~', render: (c, k) => <s key={k}>{c}</s> },
    { char: '`', render: (c, k) => <code key={k}>{c}</code> },
];

/** A line that WhatsApp draws as a bullet: "- item" or "* item" (asterisk needs the space). */
const BULLET = /^\s*[-*]\s+(.*)$/;
/** A line that WhatsApp numbers: "1. item". */
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;

/** Renders one line's inline marks; the mark that opens earliest wins (ties go to MARKS order), and code stays literal. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
    let best: { m: RegExpExecArray; opens: number; mark: (typeof MARKS)[number] } | null = null;
    for (const mark of MARKS) {
        const e = mark.char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`(^|[^\\p{L}\\p{N}_])${e}(?=\\S)([^${e}]*[^${e}\\s])${e}(?=$|[^\\p{L}\\p{N}_])`, 'u');
        const m = re.exec(text);
        if (!m) continue;
        const opens = m.index + m[1].length;
        if (!best || opens < best.opens) best = { m, opens, mark };
    }
    if (!best) return text ? [text] : [];

    // Group 1 is the boundary character, group 2 the mark's body.
    const { m, mark } = best;
    const before = text.slice(0, m.index + m[1].length);
    const after = text.slice(m.index + m[0].length);
    return [
        ...renderInline(before, `${keyPrefix}b`),
        mark.render(mark.char === '`' ? m[2] : renderInline(m[2], `${keyPrefix}i`), `${keyPrefix}m`),
        ...renderInline(after, `${keyPrefix}a`),
    ];
}

/** Renders a message body (no fences) as one block per line, with bullets, numbered rows and inline marks. */
export function renderWhatsAppText(body: string): React.ReactNode {
    if (!body) return null;

    return body.split('\n').map((line, i) => {
        const key = `l${i}`;
        const bullet = BULLET.exec(line);
        if (bullet) {
            return (
                <div key={key} className={styles.ListRow}>
                    <span aria-hidden="true">•</span>
                    <span>{renderInline(bullet[1], key)}</span>
                </div>
            );
        }
        const numbered = NUMBERED.exec(line);
        if (numbered) {
            return (
                <div key={key} className={styles.ListRow}>
                    {/* The message's own number, not a re-count: a list starting at 3 must still read 3. */}
                    <span aria-hidden="true">{numbered[1]}.</span>
                    <span>{renderInline(numbered[2], key)}</span>
                </div>
            );
        }
        // A blank line is a blank line — WhatsApp shows the gap the sender typed.
        return <div key={key}>{line ? renderInline(line, key) : ' '}</div>;
    });
}
